import { Link, useLocation, useNavigate } from 'react-router-dom'
import theme from 'lib/theme'
import { Typography, useMediaQuery } from '@mui/material'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import { useTranslation } from 'react-i18next'
import { useSideBarUiContext } from 'components/Layout/LayoutWithSecondarySidebar'
import SecondarySidebar from 'components/SecondarySidebar/SecondarySidebar'
import { SidebarLinkWrapper } from 'views/Place/Place.styled'
import IconDropdown from 'components/IconDropdown/IconDropdown'
import { usePublishProduct } from 'hooks/useMasterProducts'
import { useAlert } from 'providers/AlertProvider'
import { AxiosError } from 'axios'

export const ProductSecondarySidebar = ({
  productName,
  gtin,
  masterProductId,
}: {
  productName: string
  gtin: string
  masterProductId: string
}) => {
  const navigate = useNavigate()
  const { setShowOverlay } = useSideBarUiContext()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const location = useLocation()
  const { mutateAsync } = usePublishProduct(masterProductId as string)
  const prevListingPage = localStorage.getItem('productsListingPage')
  const { t } = useTranslation()
  const { setAlert } = useAlert()
  const options = [
    {
      key: 'descriptive-information',
      title: t('descriptiveInformation'),
      route: `/product/${masterProductId}/descriptive-information`,
    },
    {
      key: 'sales-information',
      title: t('salesInformation'),
      route: `/product/${masterProductId}/sales-information`,
    },
    {
      key: 'search-settings',
      title: t('searchSettings'),
      route: `/product/${masterProductId}/search-settings`,
    },
    {
      key: 'packaging-information',
      title: t('packagingInformation'),
      route: `/product/${masterProductId}/packaging-information`,
    },
    {
      key: 'nutritional-information',
      title: t('nutritionalInformation'),
      route: `/product/${masterProductId}/nutritional-information`,
    },
    {
      key: 'additional-information',
      title: t('additionalInformation'),
      route: `/product/${masterProductId}/additional-information`,
    },
    {
      key: 'related-products',
      title: t('relatedProducts'),
      route: `/product/${masterProductId}/related-products`,
    },
    {
      key: 'raw-data',
      title: t('rawData'),
      route: `/product/${masterProductId}/raw-data`,
    },
  ]

  const handlePublishProduct = async () => {
    await mutateAsync(
      {},
      {
        onSuccess: () => {
          setAlert(t('published'), 'success', {
            vertical: 'bottom',
            horizontal: isMobile ? 'center' : 'left',
          })
        },
        onError: (e) => {
          if (e instanceof AxiosError) {
            const errorMessage = e.response?.data?.[0]?.error?.message
            setAlert(`${t('error')}: ${errorMessage}`, 'failure', {
              vertical: 'bottom',
              horizontal: isMobile ? 'center' : 'left',
            })
          } else {
            setAlert(t('failureMessage'), 'failure', {
              vertical: 'bottom',
              horizontal: isMobile ? 'center' : 'left',
            })
          }
        },
      }
    )
  }

  return (
    <SecondarySidebar
      header={productName}
      subtitle={gtin}
      selectOptionsBtn={
        <IconDropdown
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          minWidth='160px'
          Icon={MoreHorizIcon}
          isIconOnly
          options={[
            {
              value: 'publish',
              label: t('publishProduct'),
            },
          ]}
          ariaLabel={t('productActions')}
          onChange={handlePublishProduct}
        />
      }
      onGoBackClick={() => navigate(`/product${prevListingPage}`)}>
      {options.map((option) => (
        <SidebarLinkWrapper
          key={option.key}
          optionKey={option.key}
          location={location.pathname.split('/')[3]}>
          <Link
            onClick={() => isMobile && setShowOverlay(true)}
            style={{
              textDecoration: 'none',
              color:
                location.pathname.split('/')[3] === option.key
                  ? theme.palette.primary.main
                  : theme.palette.commonColors.black,
              width: '223px',
              height: '26px',
              display: 'flex',
              alignItems: 'center',
            }}
            to={option.route}>
            <Typography variant='body2' pl={4}>
              {option.title}
            </Typography>
          </Link>
        </SidebarLinkWrapper>
      ))}
    </SecondarySidebar>
  )
}
